import { type ReactElement } from 'react';
import { defineMessages, type MessageDescriptor } from 'react-intl';
import { EditIcon } from '@m/alchemy-ui/icons/EditIcon';
import { ImageIcon } from '@m/alchemy-ui/icons/ImageIcon';
import { MediaIcon } from '@m/alchemy-ui/icons/MediaIcon';
import { RocketIcon } from '@m/alchemy-ui/icons/RocketIcon';
import { SettingsIcon } from '@m/alchemy-ui/icons/SettingsIcon';
import { PerformanceIcon } from '@m/alchemy-ui/icons/PerformanceIcon';
import { IdeaIcon } from '@m/alchemy-ui/icons/IdeaIcon';
import { PanelSizeEnum } from '../../../common/core-api';
import { type PageCmsEditorDataQuery } from '../../../graphql/queries';
import { LineDash } from '../CustomPage.styled';

export type PageCmsPanels = NonNullable<PageCmsEditorDataQuery['pageCMS']>['panels'];
export type PageCmsPanelRow = PageCmsPanels[number];
export type PageCmsPanel = PageCmsPanelRow[number];
export type PanelConfigTypename = PageCmsPanel['settings']['__typename'];
export type PanelSize = PanelSizeEnum;

export const WIDE_PANEL_SIZE = PanelSizeEnum.wide;
export const AUTO_PANEL_SIZE = PanelSizeEnum.auto;

const messages = defineMessages({
  panelTypeText: {
    id: 'editCustomPagePanelTypeText',
    defaultMessage: 'Text / HTML'
  },
  panelTypeImage: {
    id: 'editCustomPagePanelTypeImage',
    defaultMessage: 'Image'
  },
  panelTypeVideo: {
    id: 'video',
    defaultMessage: 'Video'
  },
  panelTypeChallengeCarousel: {
    id: 'editCustomPagePanelTypeChallengeCarousel',
    defaultMessage: 'Challenge Carousel'
  },
  panelTypeActivity: {
    id: 'editCustomPagePanelTypeActivity',
    defaultMessage: 'Activity Panel'
  },
  panelTypeLeaderboard: {
    id: 'leaderboard',
    defaultMessage: 'Leaderboard'
  },
  panelTypeIdeas: {
    id: 'editCustomPagePanelTypeIdeas',
    defaultMessage: 'Ideas Panel'
  },
  panelTypeBlog: {
    id: 'editCustomPagePanelTypeBlog',
    defaultMessage: 'Blog'
  },
  panelTypeLine: {
    id: 'editCustomPagePanelTypeLine',
    defaultMessage: 'Line'
  }
});

export interface PanelTypeDefinition {
  // Legacy kebab-case panel type id (the `settings.type` discriminator in page_settings JSON)
  id: string;
  message: MessageDescriptor;
  icon: ReactElement;
}

// Insertion order defines the "Add element" menu order.
export const panelTypesByTypename: Record<PanelConfigTypename, PanelTypeDefinition> = {
  TextPanelSettings: { id: 'text', message: messages.panelTypeText, icon: <EditIcon aria-hidden="true" /> },
  ImagePanelSettings: { id: 'image', message: messages.panelTypeImage, icon: <ImageIcon aria-hidden="true" /> },
  VideoPanelSettings: { id: 'video', message: messages.panelTypeVideo, icon: <MediaIcon aria-hidden="true" /> },
  ChallengeCarouselPanelSettings: {
    id: 'challenge-carousel',
    message: messages.panelTypeChallengeCarousel,
    icon: <RocketIcon aria-hidden="true" />
  },
  ActivityPanelSettings: {
    id: 'activity',
    message: messages.panelTypeActivity,
    icon: <SettingsIcon aria-hidden="true" />
  },
  LeaderboardPanelSettings: {
    id: 'leaderboard',
    message: messages.panelTypeLeaderboard,
    icon: <PerformanceIcon aria-hidden="true" />
  },
  IdeasPanelSettings: { id: 'ideas', message: messages.panelTypeIdeas, icon: <IdeaIcon aria-hidden="true" /> },
  BlogCarouselPanelSettings: {
    id: 'blog-carousel',
    message: messages.panelTypeBlog,
    icon: <RocketIcon aria-hidden="true" />
  },
  LinePanelSettings: {
    id: 'line',
    message: messages.panelTypeLine,
    icon: <LineDash aria-hidden="true">{'\u2014'}</LineDash>
  }
};

export interface PanelTypeOption extends PanelTypeDefinition {
  typename: PanelConfigTypename;
}

export const panelTypeOptions: PanelTypeOption[] = Object.entries(panelTypesByTypename).map(
  ([typename, definition]) => ({ ...definition, typename: typename as PanelConfigTypename })
);
